import { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react'
import { posterUrl, profileUrl, getMovieCast, getTVShowCast, getPersonWithCredits } from '../api/tmdb'
import { formatPlaceOfBirth } from '../utils/placeOfBirth'
import { IconTarget, IconClapper, IconTv } from './icons'

const POSTER_PH = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='342' height='513' viewBox='0 0 342 513'%3E%3Crect fill='%23333' width='342' height='513'/%3E%3Ctext fill='%23666' font-family='sans-serif' font-size='24' x='50%25' y='50%25' text-anchor='middle' dy='.3em'%3ENo Poster%3C/text%3E%3C/svg%3E"
const PROFILE_PH = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='185' height='278' viewBox='0 0 185 278'%3E%3Crect fill='%23333' width='185' height='278'/%3E%3Ccircle fill='%23555' cx='92' cy='100' r='40'/%3E%3Cellipse fill='%23555' cx='92' cy='220' rx='60' ry='50'/%3E%3C/svg%3E"
const FACE_PH = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='45' height='45' viewBox='0 0 45 45'%3E%3Crect fill='%23444' width='45' height='45' rx='22'/%3E%3C/svg%3E"

const GENRE_NAMES = {
  28: 'Action', 12: 'Adventure', 16: 'Animation', 35: 'Comedy',
  80: 'Crime', 99: 'Documentary', 18: 'Drama', 10751: 'Family',
  14: 'Fantasy', 36: 'History', 27: 'Horror', 10402: 'Music',
  9648: 'Mystery', 10749: 'Romance', 878: 'Sci-Fi', 53: 'Thriller',
  10752: 'War', 37: 'Western',
}

function computeDelta(startRect) {
  const vw = window.innerWidth
  const vh = window.innerHeight
  const finalW = Math.min(vw * 0.72, 380)
  const scale = startRect.width / finalW
  const dx = (startRect.left + startRect.width / 2) - vw / 2
  const dy = (startRect.top + startRect.height / 2) - vh / 2
  return { dx, dy, scale }
}

export default function CardOverlay({ type, data, startRect, onSelect, onDismiss, isTarget, selectLabel }) {
  const cardRef = useRef(null)
  const dismissedRef = useRef(false)
  const [bgVisible, setBgVisible] = useState(false)

  const [castData, setCastData] = useState(null)
  const [castLoading, setCastLoading] = useState(false)
  const [knownFor, setKnownFor] = useState(null)
  const [actorMeta, setActorMeta] = useState(null)

  const { dx, dy, scale } = useMemo(() => computeDelta(startRect), [startRect])

  const fromTransform = `translate(${dx}px, ${dy}px) scale(${scale}) rotateY(0deg)`
  const toTransform = `translate(0px, 0px) scale(1) rotateY(180deg)`

  useLayoutEffect(() => {
    const el = cardRef.current
    if (!el) return
    el.style.transition = 'none'
    el.style.transform = fromTransform
    void el.getBoundingClientRect()
    el.style.transition = 'transform 0.48s cubic-bezier(0.34, 1.4, 0.64, 1)'
    el.style.transform = toTransform
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const id = requestAnimationFrame(() => setBgVisible(true))
    return () => cancelAnimationFrame(id)
  }, [])

  useEffect(() => {
    if (type === 'movie') {
      setCastLoading(true)
      // TV-show bridges fetch their cast from the /tv credits endpoint.
      const fetchCast = data.mediaType === 'tv' ? getTVShowCast : getMovieCast
      fetchCast(data.id)
        .then(setCastData)
        .catch(() => setCastData([]))
        .finally(() => setCastLoading(false))
    } else {
      getPersonWithCredits(data.id)
        .then((d) => {
          const credits = (d.movie_credits?.cast || []).filter((m) => !m.adult)

          // Known For — top 3 by vote_count
          const top3 = [...credits]
            .sort((a, b) => (b.vote_count || 0) - (a.vote_count || 0))
            .slice(0, 3)
            .map((m) => m.title)
          setKnownFor(top3)

          // Career start — earliest release year
          const years = credits
            .map((m) => m.release_date && parseInt(m.release_date.slice(0, 4)))
            .filter(Boolean)
          const activeFrom = years.length > 0 ? Math.min(...years) : null

          // Top 2 genres from top 30 most popular films
          const top30 = [...credits]
            .sort((a, b) => (b.popularity || 0) - (a.popularity || 0))
            .slice(0, 30)
          const genreCounts = {}
          top30.forEach((m) => {
            ;(m.genre_ids || []).forEach((id) => {
              genreCounts[id] = (genreCounts[id] || 0) + 1
            })
          })
          const genres = Object.entries(genreCounts)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 2)
            .map(([id]) => GENRE_NAMES[Number(id)])
            .filter(Boolean)

          setActorMeta({
            place_of_birth: d.place_of_birth || null,
            activeFrom,
            genres,
          })
        })
        .catch(() => { setKnownFor([]); setActorMeta({}) })
    }
  }, [type, data.id])

  const dismiss = () => {
    if (dismissedRef.current) return
    dismissedRef.current = true
    setBgVisible(false)
    const el = cardRef.current
    if (el) {
      el.style.transition = 'transform 0.36s cubic-bezier(0.4, 0, 0.6, 1)'
      el.style.transform = fromTransform
    }
    setTimeout(onDismiss, 380)
  }

  const handleSelect = () => {
    if (type === 'movie') onSelect('movie', data, castData || [])
    else onSelect('actor', data)
  }

  const year = data.release_date?.slice(0, 4)
  const preview = castData ? castData.slice(0, 8) : []

  return (
    <div
      className={`co-backdrop ${bgVisible ? 'co-backdrop--visible' : ''}`}
      onClick={dismiss}
    >
      <div
        ref={cardRef}
        className="co-card"
        onClick={(e) => e.stopPropagation()}
      >
        {/* FRONT FACE */}
        <div className="co-face co-face--front">
          <img
            src={type === 'movie'
              ? (posterUrl(data.poster_path, 'w342') || POSTER_PH)
              : (profileUrl(data.profile_path, 'w342') || PROFILE_PH)}
            alt={type === 'movie' ? data.title : data.name}
            className="co-face__img"
            onError={(e) => { e.target.src = type === 'movie' ? POSTER_PH : PROFILE_PH }}
          />
          <div className="co-face__footer">
            <span>{type === 'movie' ? data.title : data.name}</span>
            {type === 'actor' && isTarget && <span className="co-face__target-badge"><IconTarget size={13} style={{ verticalAlign: 'middle' }} /> Goal</span>}
          </div>
        </div>

        {/* BACK FACE */}
        <div className={`co-face co-face--back ${isTarget ? 'co-face--back-target' : ''}`}>
          {type === 'movie' ? (
            <div className="co-back-inner">
              <div className="co-back-header">
                <p className="co-back-title">{data.title}</p>
                {year && <p className="co-back-year">{year}</p>}
              </div>

              <div className="co-cast-grid">
                {castLoading ? (
                  <div className="co-cast-loading"><div className="spinner spinner--sm" /></div>
                ) : preview.map((a) => (
                  <div key={a.id} className="co-cast-member" title={a.name}>
                    <img
                      src={profileUrl(a.profile_path, 'w45') || FACE_PH}
                      alt={a.name}
                      onError={(e) => { e.target.src = FACE_PH }}
                    />
                    <span>{a.name}</span>
                  </div>
                ))}
              </div>

              {castData && castData.length > 0 && (
                <p className="co-cast-count">{castData.length} in this cast</p>
              )}

              <div className="co-actions">
                <button
                  className="btn-primary co-select-btn"
                  onClick={handleSelect}
                  disabled={castLoading}
                >
                  {data.mediaType === 'tv'
                    ? <><IconTv size={15} style={{ verticalAlign: 'middle' }} /> Select Show</>
                    : <><IconClapper size={15} style={{ verticalAlign: 'middle' }} /> Select Film</>}
                </button>
                <button className="btn-ghost co-dismiss-btn" onClick={dismiss}>
                  Put It Back
                </button>
              </div>
            </div>
          ) : (
            <div className="co-back-inner">
              <p className="co-actor-name">{data.name}</p>

              {knownFor === null ? (
                <div className="co-cast-loading"><div className="spinner spinner--sm" /></div>
              ) : (
                <div className="co-actor-stats">
                  {knownFor.length > 0 && (
                    <div className="co-stat-row">
                      <span className="co-stat-label">Known For</span>
                      <span className="co-stat-value">{knownFor.join(' · ')}</span>
                    </div>
                  )}
                  {actorMeta?.activeFrom && (
                    <div className="co-stat-row">
                      <span className="co-stat-label">Active</span>
                      <span className="co-stat-value">{actorMeta.activeFrom} — Present</span>
                    </div>
                  )}
                  {actorMeta?.place_of_birth && (
                    <div className="co-stat-row">
                      <span className="co-stat-label">From</span>
                      <span className="co-stat-value">{formatPlaceOfBirth(actorMeta.place_of_birth)}</span>
                    </div>
                  )}
                  {actorMeta?.genres?.length > 0 && (
                    <div className="co-stat-row">
                      <span className="co-stat-label">Genre</span>
                      <span className="co-stat-value">{actorMeta.genres.join(' · ')}</span>
                    </div>
                  )}
                </div>
              )}

              <div className="co-actions">
                <button
                  className={`btn-primary co-select-btn ${isTarget ? 'co-select-btn--win' : ''}`}
                  onClick={handleSelect}
                >
                  {selectLabel ? selectLabel : (isTarget
                    ? <><IconTarget size={15} style={{ verticalAlign: 'middle' }} /> Select — You Win!</>
                    : <><IconClapper size={15} style={{ verticalAlign: 'middle' }} /> Select</>)}
                </button>
                <button className="btn-ghost co-dismiss-btn" onClick={dismiss}>
                  Put It Back
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
